import { useState, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'motion/react';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import Pressable from '@/components/base/Pressable';

export default function ForgotPasswordPage() {
  const navigate = useNavigate();
  const { resetPassword } = useAuth();
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';

  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [sent, setSent] = useState(false);

  const t = {
    bg: isDark ? 'bg-[#0F0F10]' : 'bg-white',
    textPrimary: isDark ? 'text-[#F5F5F7]' : 'text-neutral-900',
    textSecondary: isDark ? 'text-[#A1A1A6]' : 'text-neutral-500',
    textMuted: isDark ? 'text-[#6E6E73]' : 'text-neutral-400',
    textFaint: isDark ? 'text-[#48484F]' : 'text-neutral-300',
    cardBorder: isDark ? 'border-white/[0.06]' : 'border-neutral-200/70',
    inputBg: isDark ? 'bg-white/[0.04]' : 'bg-neutral-50',
    inputBorder: isDark ? 'border-white/[0.08]' : 'border-neutral-200',
    inputFocus: isDark ? 'focus:border-white/20 focus:bg-white/[0.06]' : 'focus:border-neutral-300 focus:bg-white',
    placeholder: isDark ? 'placeholder-[#6E6E73]' : 'placeholder-neutral-400',
    errorBg: isDark ? 'bg-red-500/10 border-red-500/20 text-red-400' : 'bg-red-50 border-red-200 text-red-600',
    successBg: isDark ? 'bg-emerald-500/10 border-emerald-500/20 text-emerald-400' : 'bg-emerald-50 border-emerald-200 text-emerald-600',
  };

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!email.trim()) return;
      setIsSubmitting(true);
      setError('');

      const { error: resetError } = await resetPassword(email.trim());
      if (resetError) {
        setError(resetError.message || 'Something went wrong. Please try again.');
        setIsSubmitting(false);
        return;
      }

      setSent(true);
      setIsSubmitting(false);
    },
    [email, resetPassword]
  );

  return (
    <div className={`study-shell study-auth min-h-[100dvh] flex items-center justify-center ${t.bg} px-5 transition-colors duration-500`} data-theme={resolvedTheme}>
      <motion.div
        className="w-full max-w-[380px]"
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.45, ease: [0.32, 0.72, 0, 1] }}
      >
        {/* Back */}
        <button
          type="button"
          onClick={() => navigate('/auth/login')}
          className={`flex items-center gap-1.5 text-xs font-medium ${t.textMuted} hover:underline mb-8 cursor-pointer`}
        >
          <i className="ri-arrow-left-line text-sm" />
          Back to Log In
        </button>

        {/* Icon + heading */}
        <div className="flex flex-col items-center mb-10">
          <div className="w-14 h-14 rounded-2xl bg-neutral-900 flex items-center justify-center mb-4">
            <div className="w-8 h-8 flex items-center justify-center">
              <i className="ri-lock-password-line text-white text-xl" />
            </div>
          </div>
          <h1 className={`text-xl font-bold ${t.textPrimary}`}>Forgot Password</h1>
          <p className={`text-sm ${t.textSecondary} mt-1 text-center`}>
            Enter your email and we'll send you a reset link
          </p>
        </div>

        {/* Messages */}
        <AnimatePresence mode="wait">
          {error && (
            <motion.div
              key="error"
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              className={`flex items-center gap-2 px-4 py-3 rounded-xl border text-xs mb-4 ${t.errorBg}`}
            >
              <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                <i className="ri-error-warning-line text-sm" />
              </div>
              {error}
            </motion.div>
          )}
          {sent && (
            <motion.div
              key="success"
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              className={`flex flex-col gap-2 px-4 py-4 rounded-xl border text-xs mb-4 ${t.successBg}`}
            >
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-mail-check-line text-sm" />
                </div>
                <span className="font-semibold">Reset link sent!</span>
              </div>
              <p className="ml-6 opacity-80">
                Check your inbox at <strong>{email}</strong>. Click the link in the email to set a new password.
              </p>
              <button
                type="button"
                onClick={() => navigate('/auth/login')}
                className="ml-6 mt-1 text-xs font-semibold underline cursor-pointer"
              >
                Back to Log In
              </button>
            </motion.div>
          )}
        </AnimatePresence>

        {!sent && (
          <form onSubmit={handleSubmit} className="space-y-3.5">
            <div>
              <label className={`block text-xs font-medium ${t.textSecondary} mb-1.5`}>Email Address</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="student@school.edu"
                autoComplete="email"
                required
                className={`w-full ${t.inputBg} border ${t.inputBorder} ${t.inputFocus} rounded-xl px-4 py-3 text-sm ${t.textPrimary} ${t.placeholder} outline-none transition-all duration-200`}
              />
            </div>

            <Pressable
              type="submit"
              disabled={isSubmitting || !email.trim()}
              className="w-full bg-neutral-900 text-white py-3.5 rounded-2xl text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-40 transition-opacity"
              scale={0.97}
            >
              {isSubmitting ? (
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-send-plane-line text-sm" />
                </div>
              )}
              <span className="whitespace-nowrap">{isSubmitting ? 'Sending...' : 'Send Reset Link'}</span>
            </Pressable>
          </form>
        )}

        <p className={`text-center text-sm ${t.textMuted} mt-6`}>
          Remember your password?{' '}
          <Link
            to="/auth/login"
            className={`font-semibold ${isDark ? 'text-white' : 'text-neutral-900'} hover:underline`}
          >
            Log In
          </Link>
        </p>

        <p className={`text-center text-xs ${t.textFaint} mt-4`}>
          <button type="button" onClick={() => navigate('/')} className="hover:underline cursor-pointer">
            Continue as guest
          </button>
        </p>
      </motion.div>
    </div>
  );
}
